import { zodResolver } from '@hookform/resolvers/zod';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { Loader2 } from 'lucide-react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { RenotifyConfirmModal } from '@apps/web/components/shifts/renotify_confirm_modal';
import {
    addressFieldsSchema,
    formatAddress,
    parseAddress,
} from '@apps/web/components/shifts/shared_shift_schema';
import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@apps/web/components/ui/form';
import { Input } from '@apps/web/components/ui/input';
import { Textarea } from '@apps/web/components/ui/textarea';
import { useOrganizationTimezone } from '@apps/web/context/timezone_provider';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { showShiftTimeUpdateToast } from '@apps/web/lib/ehr_shift_writeback_feedback';
import { type Shift } from '@libraries/typescript/api_client';
import { type components } from '@libraries/typescript/api_client/generated_api_types';

type ShiftRenotifyPreview = components['schemas']['ShiftRenotifyPreview'];

const formSchema = z
    .object({
        patientName: z.string().min(1, 'Patient name is required'),
        ...addressFieldsSchema,
        shiftStartTime: z.string().min(1, 'Start time is required'),
        shiftEndTime: z.string().min(1, 'End time is required'),
        notesForCaregiver: z.string().optional(),
        internalShiftNotes: z.string().optional(),
        agentInstructions: z.string().optional(),
    })
    .refine(
        (data) => new Date(data.shiftEndTime) > new Date(data.shiftStartTime),
        {
            message: 'End time must be after start time',
            path: ['shiftEndTime'],
        },
    );

type FormValues = z.infer<typeof formSchema>;

type EditShiftModalProps = {
    readonly isOpen: boolean;
    readonly onClose: () => void;
    readonly shift: Shift;
    readonly organizationId: string;
    readonly onSuccess: () => void;
    readonly outreachId?: string;
    readonly autoAssignFirstResponder?: boolean;
};

export function EditShiftModal({
    isOpen,
    onClose,
    shift,
    organizationId,
    onSuccess,
    outreachId,
    autoAssignFirstResponder,
}: EditShiftModalProps) {
    const { timezone } = useOrganizationTimezone();
    const [isLoadingInstructions, setIsLoadingInstructions] = useState(false);
    const [shiftUpdated, setShiftUpdated] = useState(false);
    const [generatedInstructions, setGeneratedInstructions] = useState('');
    const [renotifyPreview, setRenotifyPreview] =
        useState<ShiftRenotifyPreview | null>(null);

    const defaultValues: FormValues = useMemo(() => {
        const addressFields = parseAddress(shift.patient_address || null);

        return {
            patientName: shift.patient_name,
            ...addressFields,
            patientCity: shift.patient_city ?? '',
            patientState: shift.patient_state ?? '',
            shiftStartTime: formatInTimeZone(
                shift.shift_start_time,
                timezone,
                "yyyy-MM-dd'T'HH:mm",
            ),
            shiftEndTime: formatInTimeZone(
                shift.shift_end_time,
                timezone,
                "yyyy-MM-dd'T'HH:mm",
            ),
            notesForCaregiver: shift.notes_for_caregiver ?? '',
            internalShiftNotes: shift.internal_shift_notes ?? '',
        };
    }, [shift, timezone]);

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues,
    });

    useEffect(() => {
        if (isOpen) {
            form.reset(defaultValues);
            setShiftUpdated(false);
            setGeneratedInstructions('');
            setIsLoadingInstructions(false);
            setRenotifyPreview(null);
        }
    }, [isOpen, form, defaultValues]);

    async function onSubmit(data: FormValues) {
        try {
            const formattedData = {
                patient_name: data.patientName,
                patient_address: formatAddress(data),
                patient_city: data.patientCity || null,
                patient_state: data.patientState || null,
                shift_start_time: fromZonedTime(
                    data.shiftStartTime,
                    timezone,
                ).toISOString(),
                shift_end_time: fromZonedTime(
                    data.shiftEndTime,
                    timezone,
                ).toISOString(),
                notes_for_caregiver: data.notesForCaregiver || null,
                internal_shift_notes: data.internalShiftNotes || null,
            };

            const updateResponse = await getApiClient().patch({
                url: '/organizations/{organization_id}/shifts/{shift_id}',
                pathParams: {
                    organization_id: organizationId,
                    shift_id: shift.id,
                },
                body: formattedData,
            });

            showShiftTimeUpdateToast(updateResponse);

            if (updateResponse?.renotify_preview) {
                setRenotifyPreview(updateResponse.renotify_preview);
            }

            // If outreachId exists, generate instructions automatically
            if (outreachId) {
                setShiftUpdated(true);
                setIsLoadingInstructions(true);
                try {
                    const response = await getApiClient().post({
                        url: '/organizations/{organization_id}/shifts/{shift_id}/agent-instructions',
                        pathParams: {
                            organization_id: organizationId,
                            shift_id: shift.id,
                        },
                        body: {
                            auto_assign_first_responder:
                                autoAssignFirstResponder,
                        },
                    });

                    const instructions = response.instructions?.trim() || '';
                    setGeneratedInstructions(instructions);
                    toast.success('Agent instructions generated', {
                        persistNotification: true,
                        notificationType: 'shift_action',
                    });
                } catch (error) {
                    console.error('Failed to generate instructions:', error);
                    toast.error('Failed to generate agent instructions', {
                        persistNotification: true,
                        notificationType: 'shift_action',
                    });
                } finally {
                    setIsLoadingInstructions(false);
                }
            } else {
                onSuccess();
            }
        } catch (error) {
            console.error('Error updating shift:', error);
            toast.error('Failed to update shift', {
                persistNotification: true,
                notificationType: 'shift_action',
            });
        }
    }

    async function saveInstructions() {
        if (!outreachId || !generatedInstructions) {
            return;
        }

        try {
            await getApiClient().patch({
                url: '/organizations/{organization_id}/callouts/{callout_id}',
                pathParams: {
                    organization_id: organizationId,
                    callout_id: outreachId,
                },
                body: {
                    instructions: generatedInstructions,
                },
            });

            toast.success('Open shift instructions updated successfully', {
                persistNotification: true,
                notificationType: 'shift_action',
            });
            onSuccess();
        } catch (error) {
            console.error('Error updating callout:', error);
            toast.error('Failed to update open shift instructions', {
                persistNotification: true,
                notificationType: 'shift_action',
            });
        }
    }

    return (
        <Fragment>
            <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
                <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[700px]">
                    <DialogHeader>
                        <DialogTitle>Edit Shift</DialogTitle>
                        <DialogDescription>
                            Update the shift details below.
                        </DialogDescription>
                    </DialogHeader>
                    <Form {...form}>
                        <form
                            onSubmit={form.handleSubmit(onSubmit)}
                            className="space-y-4"
                        >
                            <FormField
                                control={form.control}
                                name="patientName"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Patient Name *</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="Enter patient name"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <div className="grid grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="shiftStartTime"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Start Time *</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type="datetime-local"
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="shiftEndTime"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>End Time *</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type="datetime-local"
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <FormField
                                control={form.control}
                                name="patientStreet"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Street Address</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="Enter street address"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <div className="grid grid-cols-3 gap-4">
                                <FormField
                                    control={form.control}
                                    name="patientCity"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>City</FormLabel>
                                            <FormControl>
                                                <Input
                                                    placeholder="Enter city"
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="patientState"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>State</FormLabel>
                                            <FormControl>
                                                <Input
                                                    placeholder="Enter state"
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="patientZipCode"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Zip Code</FormLabel>
                                            <FormControl>
                                                <Input
                                                    placeholder="Enter zip code"
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <FormField
                                control={form.control}
                                name="notesForCaregiver"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            Notes for Caregiver
                                        </FormLabel>
                                        <FormControl>
                                            <Textarea
                                                placeholder="Any special instructions or notes for the caregiver..."
                                                className="resize-none"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="internalShiftNotes"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Internal Notes</FormLabel>
                                        <FormControl>
                                            <Textarea
                                                placeholder="Internal notes for staff only..."
                                                className="resize-none"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            {outreachId && shiftUpdated && (
                                <div className="border-t pt-4">
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <h3 className="text-sm font-medium">
                                                Agent Instructions
                                            </h3>
                                            {isLoadingInstructions && (
                                                <div className="text-muted-foreground flex items-center gap-2 text-sm">
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                    Updating instructions…
                                                </div>
                                            )}
                                        </div>
                                        <Textarea
                                            value={generatedInstructions}
                                            onChange={(e) =>
                                                setGeneratedInstructions(
                                                    e.target.value,
                                                )
                                            }
                                            placeholder="Agent instructions will appear here"
                                            className="min-h-[120px]"
                                            disabled={isLoadingInstructions}
                                        />
                                        <p className="text-muted-foreground text-sm">
                                            Review and edit the generated
                                            instructions, then click “Save
                                            Instructions” to update the open
                                            shift.
                                        </p>
                                    </div>
                                </div>
                            )}
                            <DialogFooter>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={onClose}
                                    disabled={
                                        form.formState.isSubmitting ||
                                        isLoadingInstructions
                                    }
                                >
                                    Cancel
                                </Button>
                                {!shiftUpdated ? (
                                    <Button
                                        type="submit"
                                        disabled={
                                            form.formState.isSubmitting ||
                                            isLoadingInstructions ||
                                            !form.formState.isDirty
                                        }
                                    >
                                        {form.formState.isSubmitting
                                            ? 'Updating...'
                                            : 'Update Shift'}
                                    </Button>
                                ) : (
                                    outreachId && (
                                        <Button
                                            type="button"
                                            onClick={saveInstructions}
                                            disabled={isLoadingInstructions}
                                        >
                                            Save Instructions
                                        </Button>
                                    )
                                )}
                            </DialogFooter>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>
            {renotifyPreview ? (
                <RenotifyConfirmModal
                    isOpen={true}
                    onClose={() => setRenotifyPreview(null)}
                    preview={renotifyPreview}
                    shiftId={shift.id}
                    route={{
                        kind: 'organization',
                        organizationId,
                    }}
                />
            ) : null}
        </Fragment>
    );
}
